import {
  canAttemptRefresh,
  clearAuth,
  getAccessToken,
  hardReload,
  hasValidAuthToken,
  verifyRefreshNow
} from 'utils/auth';
import { SOCKET_SERVER_URL } from 'utils/urls';

import {
  mapWaitlistEntry,
  WaitlistEntry,
  WaitlistFetchRequest,
  WaitlistJoinRequest,
  WaitlistLeaveRequest,
  WaitlistUpdateRequest
} from 'types/waitlist';

/**
 * Resolve the engine HTTP base URL. SOCKET_SERVER_URL may be a ws(s):// URL —
 * convert to http(s):// for REST and strip trailing slashes. This mirrors the
 * pattern used by the old WaitlistService and matches what Unity hits.
 */
const getEngineBase = (): string => {
  const raw = process.env.REACT_APP_ENGINE_API_URL ?? SOCKET_SERVER_URL ?? '';
  return raw.replace(/^wss?:/, 'https:').replace(/\/+$/, '');
};

const authHeaders = (): Record<string, string> => {
  const jwt = getAccessToken();
  return jwt ? { Authorization: `Bearer ${jwt}` } : {};
};

export class WaitlistServiceError extends Error {
  constructor(message: string, readonly status: number, readonly body?: unknown) {
    super(message);
    this.name = 'WaitlistServiceError';
  }
}

const parseJsonSafely = async (res: Response): Promise<unknown> => {
  try {
    return await res.json();
  } catch {
    return undefined;
  }
};

/**
 * True when the engine's `verifyToken` middleware rejected the request for a
 * missing/expired/invalid token, as opposed to the waitlist controller's own
 * business-logic failures (already-on-waitlist, invalid buy-in, full table,
 * etc.), which ALSO use status 400. The distinguishing signal isn't the
 * status code — it's the body shape: verifyToken sends a plain-text body
 * (`res.send('Access Denied')` / `res.send('Invalid Token: ...')`), which
 * fails to parse as JSON, while every waitlistController response is real
 * JSON (`res.json({ success, message })`). Checking status alone would
 * reload the page on a routine business error too.
 */
const isAuthFailure = (res: Response, body: unknown): boolean =>
  res.status === 401 || (res.status === 400 && body === undefined);

/**
 * The engine rejected this request's token. If the CLIENT's own view of the
 * session still says the token is valid (within REFRESH_MARGIN_MS or a
 * refresh is currently in flight), the disagreement is transient — the
 * refresh scheduler either has the situation in hand or is about to. Kick
 * it explicitly so the next REST call carries a fresh token, and DO NOT
 * nuke the session on this transient 401 (which is exactly what caused the
 * "couple of seconds" logout after a Method A repro: waitlist polling hit
 * a 401 within seconds, and the old teardown-on-401 raced the client-side
 * refresh cycle).
 *
 * Only when the session is genuinely beyond saving — no live refresh token, or
 * a family Descope has already repudiated — do we hard-reload; a real ended
 * session still needs to bounce the SPA to drop stale Redux/subscription state.
 *
 * `canAttemptRefresh()` is half of that test because `hasValidAuthToken()`
 * alone inverts the decision: it is false exactly when the session token has
 * expired, which is the one case a 401 is EXPECTED and a refresh is the whole
 * remedy. Testing it alone meant this handler refreshed when there was least
 * reason to and tore the session down when there was most.
 */
const handleAuthFailure = (): void => {
  if (hasValidAuthToken() || canAttemptRefresh()) {
    console.warn(
      '[Waitlist] REST 401 but session still refreshable — verifying with Descope now, not tearing down'
    );
    verifyRefreshNow();
    return;
  }
  clearAuth();
  hardReload();
};

const ensureOk = async (res: Response, op: string): Promise<unknown> => {
  const body = await parseJsonSafely(res);
  if (!res.ok) {
    if (isAuthFailure(res, body)) {
      handleAuthFailure();
    }
    throw new WaitlistServiceError(`${op} failed with status ${res.status}`, res.status, body);
  }
  return body;
};

export const joinWaitlist = async (req: WaitlistJoinRequest): Promise<WaitlistEntry | null> => {
  const res = await fetch(`${getEngineBase()}/api/waitlist`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders()
    },
    body: JSON.stringify({
      gameId: req.gameId,
      gameType: req.gameType,
      playerGuid: req.playerGuid,
      autoBuyIn: req.autoBuyIn ?? 0
    })
  });
  const body = await ensureOk(res, 'joinWaitlist');
  return mapWaitlistEntry(body);
};

/**
 * Set/update the auto buy-in on an already-queued waitlist entry (player
 * pre-setting their buy-in before a seat opens). Pass `autoBuyIn: 0` to clear.
 */
export const updateWaitlistAutoBuyIn = async (
  req: WaitlistUpdateRequest
): Promise<WaitlistEntry | null> => {
  const res = await fetch(`${getEngineBase()}/api/waitlist`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders()
    },
    body: JSON.stringify({
      gameId: req.gameId,
      gameType: req.gameType,
      playerGuid: req.playerGuid,
      autoBuyIn: req.autoBuyIn
    })
  });
  const body = await ensureOk(res, 'updateWaitlistAutoBuyIn');
  return mapWaitlistEntry(body);
};

export const leaveWaitlist = async (req: WaitlistLeaveRequest): Promise<void> => {
  const { gameType, gameId, playerGuid } = req;
  const res = await fetch(
    `${getEngineBase()}/api/waitlist/${encodeURIComponent(gameType)}/player/${encodeURIComponent(
      String(gameId)
    )}/${encodeURIComponent(playerGuid)}`,
    {
      method: 'DELETE',
      headers: authHeaders()
    }
  );
  await ensureOk(res, 'leaveWaitlist');
};

export const fetchMyWaitlists = async (req: WaitlistFetchRequest): Promise<WaitlistEntry[]> => {
  const { gameType, playerGuid } = req;
  const res = await fetch(
    `${getEngineBase()}/api/waitlist/${encodeURIComponent(gameType)}/player/${encodeURIComponent(
      playerGuid
    )}`,
    { headers: authHeaders() }
  );
  const body = await ensureOk(res, 'fetchMyWaitlists');
  if (!body || typeof body !== 'object') return [];
  const players = (body as { players?: unknown }).players;
  if (!Array.isArray(players)) return [];
  return players.map(mapWaitlistEntry).filter((e): e is WaitlistEntry => e !== null);
};
